import { cn } from "@/src/lib/utils";

export interface SpinningTextRingProps {
  /** Phrase wrapping the full ring, in one continuous line. */
  text: string;
  /** Rendered diameter in px. */
  size?: number;
  /** Switches to a light color scheme for use over a dark background. */
  isDark?: boolean;
  className?: string;
}

const NBSP = " ";

/*
 * Geometry note — easy to get backwards, so spelled out precisely.
 *
 * Each character sits at the tip of a "spoke" div: pinned at the ring's
 * center via `left/top: 50%`, `height: spokeLength`, rotated around its
 * own TOP edge (`transform-origin: top center` — the one point on the
 * spoke that stays fixed at center regardless of angle), with the glyph
 * flex-anchored to its BOTTOM (far) end.
 *
 * Because the spoke's un-rotated box grows *downward* from that pivot
 * (ordinary box flow), `rotate(0deg)` points the spoke straight down —
 * angleDeg 0 is the BOTTOM of the circle (6 o'clock), not the top. Working
 * out the full transform gives the glyph's on-screen offset from center as
 *   (x, y) = (-spokeLength * sin(angleDeg), spokeLength * cos(angleDeg))
 * so angleDeg 0 -> bottom, 90 -> left, 180 -> top, 270 -> right, sweeping
 * clockwise as angleDeg increases, STARTING from the bottom.
 *
 * This ring wraps ONE continuous phrase around the full circle in a
 * single direction — classic coin/badge-edge style: legible only where
 * the text currently sits at the top, upside-down 180deg away. That's
 * intentional, not a bug — a ring that spins can't keep text upright
 * everywhere at once without either freezing the rotation or having each
 * letter counter-rotate against it, which would just cancel the spin
 * visually. Position starts the sweep at the top (angleDeg = 180 + i *
 * step) so the phrase's first character is the one that lands in the
 * readable zone. The glyph itself then needs a constant -180deg
 * correction, since the spoke's own un-rotated orientation naturally
 * points outward-and-down (upright at the *bottom*), not outward-and-up.
 */
const ROTATION_CORRECTION_DEG = -180;

/**
 * Decorative, non-interactive text ring that spins around whatever it's
 * placed over (e.g. a button) — it renders nothing in its own center, so
 * it's meant to be centered on top of that element via `absolute inset-0
 * m-auto` from a `position: relative` ancestor, not to replace it.
 * `pointer-events: none` is baked in so it never blocks clicks to whatever
 * sits underneath.
 *
 * Characters are placed individually rather than via SVG `<textPath>` —
 * textPath needs `textLength` to force arbitrary text to span an exact
 * circumference, and stretching/compressing arbitrary phrases to fit
 * rendered inconsistently (glyphs bunching into each other) across
 * browsers. Per-character placement gives every glyph a fixed,
 * evenly-spaced angular slot instead, so spacing is exact and predictable
 * regardless of the phrase's length.
 */
export function SpinningTextRing({
  text,
  size = 120,
  isDark = false,
  className,
}: SpinningTextRingProps) {
  const chars = Array.from(text);
  const anglePerChar = 360 / chars.length;
  // A few px beyond size/2 — same character count spread over a slightly
  // larger circumference, so each glyph gets more breathing room without
  // needing the caller's `size` (and everything centered against it) to
  // change. Harmless to extend past the nominal box: nothing clips it.
  const spokeLength = size / 2 + 6;

  return (
    <div
      aria-hidden
      className={cn(
        "spinning-text-ring pointer-events-none absolute inset-0 m-auto",
        className,
      )}
      style={{ width: size, height: size }}
    >
      {chars.map((char, index) => {
        const angleDeg = 180 + index * anglePerChar;
        return (
          <div
            key={index}
            className="absolute left-1/2 top-1/2 flex flex-col items-center justify-end"
            style={{
              height: spokeLength,
              transform: `translateX(-50%) rotate(${angleDeg}deg)`,
              transformOrigin: "top center",
            }}
          >
            <span
              className={cn(
                "text-[10px] font-light uppercase leading-none transition-colors duration-300",
                isDark
                  ? "text-neutral-300 group-hover:text-white"
                  : "text-neutral-500 group-hover:text-neutral-700",
              )}
              style={{
                display: "inline-block",
                transform: `rotate(${ROTATION_CORRECTION_DEG}deg)`,
              }}
            >
              {/* A lone space text node inside an inline element can get
                  collapsed by HTML whitespace rules, silently dropping
                  that glyph's slot — NBSP always renders. */}
              {char === " " ? NBSP : char}
            </span>
          </div>
        );
      })}
    </div>
  );
}
